import type { ToolCallApprovalPayload } from "@/components/assistant/findActiveWriteApproval";
import { patchPendingWriteToolProposalInMessages } from "@/components/assistant/patchPendingWriteToolProposalInMessages";
import type { WriteApprovalEntityKind } from "@/components/assistant/formatWriteApprovalSummary";
import type { EditablePlanControls } from "@/components/assistant/AssistantPlanContext";
import {
  draftFingerprintForKind,
  mergePlanDraftForToolInput,
  planDraftFromProposal,
  proposalFingerprintForKind,
  supportsEditableWriteApprovalPlan,
  type WriteApprovalPlanDraft,
} from "@/components/assistant/writeApprovalEditablePlanFields";
import type { UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";

type ActivePending = {
  toolCallId: string;
  approvalReady: boolean;
  proposal: Record<string, unknown>;
};

type Args = {
  kind: WriteApprovalEntityKind;
  activePending: ActivePending | null;
  isWriteToolName: (toolName: string) => boolean;
  setMessages: Dispatch<SetStateAction<UIMessage[]>>;
  setApprovalByToolCallId: Dispatch<
    SetStateAction<Record<string, ToolCallApprovalPayload>>
  >;
  enrichProposal?: (proposal: Record<string, unknown>) => Record<string, unknown>;
};

export function useWriteApprovalPlanDraftSync({
  kind,
  activePending,
  isWriteToolName,
  setMessages,
  setApprovalByToolCallId,
  enrichProposal,
}: Args): {
  editablePlan: EditablePlanControls | undefined;
  mergePlanDraftForApprove: () => Record<string, unknown>;
} {
  const enabled = supportsEditableWriteApprovalPlan(kind);

  const resolvedProposal = useMemo(() => {
    if (!activePending) return null;
    return enrichProposal
      ? enrichProposal(activePending.proposal)
      : activePending.proposal;
  }, [activePending, enrichProposal]);
  const [planDraft, setPlanDraft] = useState<WriteApprovalPlanDraft>({});
  const [assistantProposalConflict, setAssistantProposalConflict] = useState(false);
  const planDirtyRef = useRef(false);
  const lastProposalFingerprintRef = useRef("");
  const lastPendingToolCallIdRef = useRef("");
  const pendingAssistantProposalRef = useRef<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!enabled || !activePending || !resolvedProposal) {
      setPlanDraft({});
      setAssistantProposalConflict(false);
      planDirtyRef.current = false;
      lastProposalFingerprintRef.current = "";
      lastPendingToolCallIdRef.current = "";
      pendingAssistantProposalRef.current = null;
      return;
    }

    const fp = proposalFingerprintForKind(kind, resolvedProposal);
    if (activePending.toolCallId !== lastPendingToolCallIdRef.current) {
      lastPendingToolCallIdRef.current = activePending.toolCallId;
      lastProposalFingerprintRef.current = fp;
      pendingAssistantProposalRef.current = null;
      setAssistantProposalConflict(false);
      setPlanDraft(planDraftFromProposal(kind, resolvedProposal));
      planDirtyRef.current = false;
      return;
    }

    if (fp === lastProposalFingerprintRef.current) return;

    if (planDirtyRef.current && fp !== draftFingerprintForKind(kind, planDraft)) {
      pendingAssistantProposalRef.current = resolvedProposal;
      setAssistantProposalConflict(true);
      return;
    }

    lastProposalFingerprintRef.current = fp;
    pendingAssistantProposalRef.current = null;
    setAssistantProposalConflict(false);
    setPlanDraft(planDraftFromProposal(kind, resolvedProposal));
    planDirtyRef.current = false;
  }, [enabled, kind, activePending?.toolCallId, resolvedProposal]);

  useEffect(() => {
    if (!enabled || !activePending?.approvalReady || !activePending.toolCallId) return;
    const merged = mergePlanDraftForToolInput(kind, planDraft);
    const toolCallId = activePending.toolCallId;
    const handle = window.setTimeout(() => {
      lastProposalFingerprintRef.current = draftFingerprintForKind(kind, planDraft);
      setMessages((prev) =>
        patchPendingWriteToolProposalInMessages({
          messages: prev,
          toolCallId,
          isWriteToolName,
          proposalPatch: merged,
        })
      );
      setApprovalByToolCallId((prev) => {
        const row = prev[toolCallId];
        if (!row) return prev;
        return {
          ...prev,
          [toolCallId]: { ...row, args: { ...row.args, ...merged } },
        };
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [
    enabled,
    kind,
    planDraft,
    activePending?.approvalReady,
    activePending?.toolCallId,
    isWriteToolName,
    setMessages,
    setApprovalByToolCallId,
  ]);

  const handlePlanDraftChange = useCallback((draft: WriteApprovalPlanDraft) => {
    planDirtyRef.current = true;
    setPlanDraft(draft);
  }, []);

  const handleApplyAssistantProposal = useCallback(() => {
    const proposal = pendingAssistantProposalRef.current;
    if (!proposal) return;
    setPlanDraft(planDraftFromProposal(kind, proposal));
    planDirtyRef.current = false;
    setAssistantProposalConflict(false);
    lastProposalFingerprintRef.current = proposalFingerprintForKind(kind, proposal);
    pendingAssistantProposalRef.current = null;
  }, [kind]);

  const handleKeepLocalEdits = useCallback(() => {
    const proposal = pendingAssistantProposalRef.current;
    setAssistantProposalConflict(false);
    pendingAssistantProposalRef.current = null;
    if (proposal) {
      lastProposalFingerprintRef.current = proposalFingerprintForKind(kind, proposal);
    }
  }, [kind]);

  const mergePlanDraftForApprove = useCallback(() => {
    return mergePlanDraftForToolInput(kind, planDraft);
  }, [kind, planDraft]);

  const editablePlan = useMemo((): EditablePlanControls | undefined => {
    if (!enabled || !activePending) return undefined;
    return {
      draft: planDraft,
      onDraftChange: handlePlanDraftChange,
      assistantProposalConflict,
      onApplyAssistantProposal: handleApplyAssistantProposal,
      onKeepLocalEdits: handleKeepLocalEdits,
    };
  }, [
    enabled,
    activePending,
    planDraft,
    handlePlanDraftChange,
    assistantProposalConflict,
    handleApplyAssistantProposal,
    handleKeepLocalEdits,
  ]);

  return { editablePlan, mergePlanDraftForApprove };
}
